import { lazy, onMount, Suspense, type ComponentProps, type ParentProps } from "solid-js";
import { FileComponentProvider } from "@mux/ui/context/file";
import { MarkedProvider } from "@mux/ui/context/marked";
import type { File as FileViewer } from "@mux/ui/file";
import { preloadMarkdown } from "@/components/lazy-markdown";

const LazyFile = lazy(() =>
  import("@mux/ui/file").then((module) => ({ default: module.File })),
);

/**
 * Loads the viewer chunk inside its own boundary. A surface's nearest boundary
 * (a Grid window's) would otherwise replace its whole transcript on the first
 * diff, discarding the scroll position.
 */
function File(props: ComponentProps<typeof FileViewer>) {
  return (
    <Suspense fallback={null}>
      <LazyFile {...props} />
    </Suspense>
  );
}

/**
 * Warms the file viewer chunk so the first file open in a workspace renders
 * without waiting. Safe to call repeatedly; the module promise is cached.
 */
export function preloadFileViewer() {
  void LazyFile.preload().catch(() => undefined);
}

export default function MachineContentProviders(props: ParentProps) {
  // Mounted once for the whole dashboard, whose chats render Markdown: fetch
  // the renderer alongside the workers MarkedProvider starts, not when the
  // first message draws.
  onMount(preloadMarkdown);
  return (
    <MarkedProvider>
      <FileComponentProvider component={File}>
        {props.children}
      </FileComponentProvider>
    </MarkedProvider>
  );
}
